import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";
import { randomId } from "@/shared/utils/randomId";

const idempotencyHeader = (key: string) => ({ headers: { "Idempotency-Key": key } });

export const generateDispatchIdempotencyKey = (): string => `dispatch:${randomId()}`;

const toNumber = (value: unknown): number => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

type NumericWireValue = number | string | null | undefined;
type DispatchRecordItemWire = Omit<DispatchRecordItemDto, "traysDispatched"> & {
  traysDispatched?: NumericWireValue;
  quantityDispatched?: NumericWireValue;
  productName?: string;
};
type TrayReturnWire = Omit<TrayReturnDto, "quantityReturned" | "recordedAt"> & {
  quantityReturned?: NumericWireValue;
  recordedAt?: string;
  createdAt?: string;
};
type DispatchPaymentWire = Omit<DispatchPaymentDto, "amount"> & {
  amount?: NumericWireValue;
};
type DispatchRecordWire = Omit<
  DispatchRecordDto,
  | "totalTraysDispatched"
  | "totalTraysReturned"
  | "traysOutstanding"
  | "orderTotalAmount"
  | "amountPaid"
  | "balanceAmount"
  | "payments"
  | "items"
  | "trayReturns"
> & {
  totalTraysDispatched?: NumericWireValue;
  totalQuantityDispatched?: NumericWireValue;
  totalTraysReturned?: NumericWireValue;
  totalQuantityReturned?: NumericWireValue;
  traysOutstanding?: NumericWireValue;
  quantityOutstanding?: NumericWireValue;
  orderTotalAmount?: NumericWireValue;
  amountPaid?: NumericWireValue;
  balanceAmount?: NumericWireValue;
  requiresTrayReturn?: boolean;
  payments?: DispatchPaymentWire[];
  items?: DispatchRecordItemWire[];
  trayReturns?: TrayReturnWire[];
};
type DispatchSummaryItemWire = Omit<DispatchSummaryItemDto, "quantityDispatched"> & {
  quantityDispatched?: NumericWireValue;
};
type DispatchRecordSummaryWire = Omit<
  DispatchRecordSummaryDto,
  "totalTraysDispatched" | "totalTraysReturned" | "traysOutstanding" | "items"
> & {
  totalTraysDispatched?: NumericWireValue;
  totalQuantityDispatched?: NumericWireValue;
  totalTraysReturned?: NumericWireValue;
  totalQuantityReturned?: NumericWireValue;
  traysOutstanding?: NumericWireValue;
  quantityOutstanding?: NumericWireValue;
  items?: DispatchSummaryItemWire[];
};
type OutstandingTrayWire = Omit<
  OutstandingTrayDto,
  "totalTraysDispatched" | "totalTraysReturned" | "traysOutstanding"
> & {
  totalTraysDispatched?: NumericWireValue;
  totalQuantityDispatched?: NumericWireValue;
  totalTraysReturned?: NumericWireValue;
  totalQuantityReturned?: NumericWireValue;
  traysOutstanding?: NumericWireValue;
  quantityOutstanding?: NumericWireValue;
};
type DeliveryNoteLineWire = Omit<DeliveryNoteLineItemDto, "traysDispatched"> & {
  productName?: string;
  traysDispatched?: NumericWireValue;
  quantityDispatched?: NumericWireValue;
};
type DeliveryNoteWire = Omit<DeliveryNoteDto, "lineItems" | "totalTrays"> & {
  lineItems?: DeliveryNoteLineWire[];
  items?: DeliveryNoteLineWire[];
  totalTrays?: NumericWireValue;
  totalQuantity?: NumericWireValue;
};
type PagedWire<T> = {
  items?: T[];
  totalCount?: number;
  pageNumber?: number;
  pageSize?: number;
  totalPages?: number;
};

const normalizeDispatchItem = (item: DispatchRecordItemWire): DispatchRecordItemDto => ({
  id: item.id,
  orderItemId: item.orderItemId,
  cropTypeName: item.cropTypeName ?? item.productName ?? "",
  variety: item.variety,
  trayType: item.trayType,
  greenhouseStockEntryId: item.greenhouseStockEntryId,
  batchNumber: item.batchNumber,
  traysDispatched: toNumber(item.traysDispatched ?? item.quantityDispatched),
  unit: item.unit,
  unitName: item.unitName,
  seedlingCount: item.seedlingCount ?? null,
  orderedQuantity: toNumber(item.orderedQuantity),
  previouslyDeliveredQuantity: toNumber(item.previouslyDeliveredQuantity),
  plannedQuantity: toNumber(item.plannedQuantity ?? item.quantityDispatched),
  remainingAfterPlannedQuantity: toNumber(item.remainingAfterPlannedQuantity),
  bedAllocationRequiredQuantity: toNumber(item.bedAllocationRequiredQuantity ?? item.quantityDispatched),
  handoverBedOptions: item.handoverBedOptions ?? [],
  handoverBedSelections: item.handoverBedSelections ?? [],
});

const normalizeTrayReturn = (trayReturn: TrayReturnWire): TrayReturnDto => ({
  id: trayReturn.id,
  dispatchRecordId: trayReturn.dispatchRecordId,
  returnDate: trayReturn.returnDate,
  quantityReturned: toNumber(trayReturn.quantityReturned),
  notes: trayReturn.notes,
  recordedByUserName: trayReturn.recordedByUserName,
  recordedAt: trayReturn.recordedAt ?? trayReturn.createdAt ?? "",
  status: trayReturn.status ?? "Pending",
});

const normalizeDispatchRecord = (record: DispatchRecordWire): DispatchRecordDto => ({
  ...record,
  assignmentInheritedFromPlan: record.assignmentInheritedFromPlan ?? false,
  totalTraysDispatched: toNumber(record.totalTraysDispatched ?? record.totalQuantityDispatched),
  totalTraysReturned: toNumber(record.totalTraysReturned ?? record.totalQuantityReturned),
  traysOutstanding: toNumber(record.traysOutstanding ?? record.quantityOutstanding),
  orderTotalAmount: toNumber(record.orderTotalAmount),
  amountPaid: toNumber(record.amountPaid),
  balanceAmount: toNumber(record.balanceAmount),
  requiresTrayReturn: record.requiresTrayReturn ?? true,
  payments: (record.payments ?? []).map((p) => ({
    paymentNumber: p.paymentNumber,
    amount: toNumber(p.amount),
    paymentMethod: p.paymentMethod,
    paymentReference: p.paymentReference,
    receiptNumber: p.receiptNumber,
    paymentDate: p.paymentDate,
  })),
  items: (record.items ?? []).map(normalizeDispatchItem),
  trayReturns: (record.trayReturns ?? []).map(normalizeTrayReturn),
});

const normalizeDispatchSummaryItem = (item: DispatchSummaryItemWire): DispatchSummaryItemDto => ({
  productName: item.productName ?? "",
  variety: item.variety,
  quantityDispatched: toNumber(item.quantityDispatched),
  seedlingCount: item.seedlingCount ?? null,
});

const normalizeDispatchSummary = (summary: DispatchRecordSummaryWire): DispatchRecordSummaryDto => ({
  ...summary,
  totalTraysDispatched: toNumber(summary.totalTraysDispatched ?? summary.totalQuantityDispatched),
  totalTraysReturned: toNumber(summary.totalTraysReturned ?? summary.totalQuantityReturned),
  traysOutstanding: toNumber(summary.traysOutstanding ?? summary.quantityOutstanding),
  dispatchedAt: summary.dispatchedAt,
  requiresTrayReturn: summary.requiresTrayReturn ?? true,
  items: (summary.items ?? []).map(normalizeDispatchSummaryItem),
});

const normalizeOutstandingTray = (row: OutstandingTrayWire): OutstandingTrayDto => ({
  ...row,
  totalTraysDispatched: toNumber(row.totalTraysDispatched ?? row.totalQuantityDispatched),
  totalTraysReturned: toNumber(row.totalTraysReturned ?? row.totalQuantityReturned),
  traysOutstanding: toNumber(row.traysOutstanding ?? row.quantityOutstanding),
});

const normalizeDeliveryNoteLine = (item: DeliveryNoteLineWire): DeliveryNoteLineItemDto => ({
  cropTypeName: item.cropTypeName ?? item.productName ?? "",
  variety: item.variety,
  trayType: item.trayType,
  traysDispatched: toNumber(item.traysDispatched ?? item.quantityDispatched),
});

const normalizeDeliveryNote = (note: DeliveryNoteWire): DeliveryNoteDto => ({
  ...note,
  lineItems: (note.lineItems ?? note.items ?? []).map(normalizeDeliveryNoteLine),
  totalTrays: toNumber(note.totalTrays ?? note.totalQuantity),
});

// ── Types ──────────────────────────────────────────────────────────────────

export interface DispatchRecordItemDto {
  id: string;
  orderItemId: string;
  cropTypeName: string;
  variety?: string;
  trayType?: string;
  greenhouseStockEntryId: string;
  batchNumber?: string;
  traysDispatched: number;
  unit?: string;
  unitName?: string;
  seedlingCount?: number | null;
  orderedQuantity: number;
  previouslyDeliveredQuantity: number;
  plannedQuantity: number;
  remainingAfterPlannedQuantity: number;
  bedAllocationRequiredQuantity: number;
  handoverBedOptions: DispatchHandoverBedOptionDto[];
  handoverBedSelections: DispatchHandoverBedSelectionDto[];
}

export interface DispatchHandoverBedOptionDto {
  greenhouseStockEntryBedId: string;
  greenhouseStockEntryId: string;
  greenhouseBedId: string;
  greenhouseName: string;
  bayName: string;
  bedNumber: string;
  availableTrays: number;
  isSystemReservedLocation?: boolean;
}

export interface DispatchHandoverBedSelectionDto extends Omit<DispatchHandoverBedOptionDto, "availableTrays"> {
  trayQuantity: number;
}

export interface TrayReturnDto {
  id: string;
  dispatchRecordId: string;
  dispatchNumber?: string;
  orderNumber?: string;
  customerName?: string;
  returnDate: string;
  quantityReturned: number;
  notes?: string;
  recordedByUserName: string;
  recordedAt: string;
  status: string;
  approvedByUserName?: string;
  approvedAt?: string;
  approvalNotes?: string;
  rejectionReason?: string;
}

export interface DispatchRecordDto {
  id: string;
  dispatchNumber: string;
  orderId: string;
  orderNumber: string;
  customerName: string;
  fulfilmentMode: string;
  status: string;
  deliveryPlanId?: string;
  deliveryPlanName?: string;
  plannedRouteName?: string;
  assignmentInheritedFromPlan: boolean;
  // Level 1 — Growing Supervisor handover
  handedOverByUserName?: string;
  handedOverAt?: string;
  acknowledgedByUserName?: string;
  acknowledgedAt?: string;
  handoverNotes?: string;
  // Level 2 — Dispatch to customer
  dispatchedByUserName?: string;
  dispatchedAt?: string;
  collectedByName?: string;
  collectedByIdOrPhone?: string;
  driverId?: string;
  driverUserId?: string;
  vehicleNumber?: string;
  driverName?: string;
  driverPhone?: string;
  expectedDeliveryDate?: string;
  actualDeliveryDate?: string;
  deliveredToName?: string;
  // Tray accounting
  totalTraysDispatched: number;
  totalTraysReturned: number;
  traysOutstanding: number;
  // Delivery note
  deliveryNoteNumber?: string;
  deliveryNoteGeneratedAt?: string;
  // Payment snapshot
  orderTotalAmount: number;
  amountPaid: number;
  balanceAmount: number;
  payments: DispatchPaymentDto[];
  requiresTrayReturn: boolean;
  // Relations
  items: DispatchRecordItemDto[];
  trayReturns: TrayReturnDto[];
  createdAt: string;
}

export interface DispatchPaymentDto {
  paymentNumber: string;
  amount: number;
  paymentMethod: string;
  paymentReference?: string;
  receiptNumber?: string;
  paymentDate: string;
}

export interface DispatchSummaryItemDto {
  productName: string;
  variety?: string;
  quantityDispatched: number;
  seedlingCount: number | null;
}

export interface DispatchRecordSummaryDto {
  id: string;
  dispatchNumber: string;
  orderId: string;
  orderNumber: string;
  customerName: string;
  fulfilmentMode: string;
  status: string;
  deliveryPlanId?: string;
  deliveryPlanName?: string;
  assignmentInheritedFromPlan: boolean;
  driverName?: string;
  driverPhone?: string;
  vehicleNumber?: string;
  expectedDeliveryDate?: string;
  totalTraysDispatched: number;
  totalTraysReturned: number;
  traysOutstanding: number;
  dispatchedAt?: string;
  createdAt: string;
  items: DispatchSummaryItemDto[];
  requiresTrayReturn: boolean;
}

export interface OutstandingTrayDto {
  dispatchRecordId: string;
  dispatchNumber: string;
  orderId: string;
  orderNumber: string;
  customerName: string;
  customerPhone: string;
  customerCounty: string;
  totalTraysDispatched: number;
  totalTraysReturned: number;
  traysOutstanding: number;
  dispatchedAt: string;
}

export interface DeliveryNoteLineItemDto {
  cropTypeName: string;
  variety?: string;
  trayType?: string;
  traysDispatched: number;
}

export interface DeliveryNoteDto {
  deliveryNoteNumber: string;
  dispatchNumber: string;
  generatedAt: string;
  customerName: string;
  fulfilmentMode: string;
  collectedByName?: string;
  vehicleNumber?: string;
  driverName?: string;
  expectedDeliveryDate?: string;
  lineItems: DeliveryNoteLineItemDto[];
  totalTrays: number;
}

export interface CompensationNoteSummaryDto {
  id: string;
  compensationNoteNumber: string;
  orderId: string;
  orderNumber: string;
  customerName: string;
  dispatchNumber: string;
  trigger: string;
  affectedTrayCount: number;
  status: string;
  raisedAt: string;
}

export interface CompensationNoteDto extends CompensationNoteSummaryDto {
  dispatchRecordId: string;
  dispatchRecordItemId: string;
  affectedProductName: string;
  affectedVariety?: string;
  reason: string;
  notes?: string;
  raisedByUserName: string;
  reviewedByUserName?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  rejectionReason?: string;
  resolution?: string;
  quantityFilledFromReserve?: number;
  quantityCreditNoted?: number;
  creditNoteId?: string;
  creditNoteNumber?: string;
}

// ── Request types ──────────────────────────────────────────────────────────

export interface InitiateDispatchItemRequest {
  orderItemId: string;
  deliveryPlanStopItemId?: string;
  greenhouseStockEntryId: string;
  quantityDispatched: number;
}

export interface InitiateDispatchRequest {
  orderId: string;
  fulfilmentMode: string; // "CustomerCollection" | "Delivery"
  items: InitiateDispatchItemRequest[];
  notes?: string;
  deliveryPlanId?: string;
}

export interface ConfirmHandoverRequest {
  notes?: string;
  itemAllocations: Array<{
    dispatchRecordItemId: string;
    bedAllocations: Array<{
      greenhouseStockEntryBedId: string;
      trayQuantity: number;
    }>;
  }>;
}

export interface AcknowledgeReceiptRequest {
  notes?: string;
}

export interface CompleteDispatchRequest {
  collectedByName?: string;
  collectedByIdOrPhone?: string;
  vehicleNumber?: string;
  driverName?: string;
  driverPhone?: string;
  expectedDeliveryDate?: string;
  notes?: string;
  /** Links the dispatch to a User account with the Driver role for mobile trip lookup. */
  driverUserId?: string;
}

export interface ConfirmDeliveryRequest {
  actualDeliveryDate: string;
  deliveredToName?: string;
  notes?: string;
}

export interface RecordTrayReturnRequest {
  dispatchRecordId: string;
  returnDate: string;
  quantityReturned: number;
  notes?: string;
}

export interface RaiseCompensationNoteRequest {
  dispatchRecordId: string;
  dispatchRecordItemId: string;
  affectedTrayCount: number;
  reason: string;
  notes?: string;
}

export interface ApproveCompensationNoteRequest {
  compensationNoteId: string;
  reviewNotes?: string;
}

export interface RejectCompensationNoteRequest {
  compensationNoteId: string;
  rejectionReason: string;
}

export interface CancelDispatchRequest {
  reason: string;
}

export interface DispatchHistoryListResult {
  items: DispatchRecordSummaryDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface TrayReturnHistorySummaryDto {
  id: string;
  dispatchRecordId: string;
  dispatchNumber: string;
  orderId: string;
  orderNumber: string;
  customerName: string;
  returnDate: string;
  quantityReturned: number;
  notes?: string;
  recordedByUserName: string;
  createdAt: string;
}

export interface TrayReturnHistoryListResult {
  items: TrayReturnHistorySummaryDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface OutstandingTrayListResult {
  items: OutstandingTrayDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface DispatchPreparationBatchDto {
  stockBatchId: string;
  batchReference: string;
  greenhouse?: string;
  readyDate?: string;
  sowingDate?: string;
  trayType?: string;
  trayCapacity?: number;
  physicalBaseQuantity: number;
  reservedBaseQuantity: number;
  fillingReserveBaseQuantity: number;
  openAllocationBaseQuantity: number;
  dispatchableBaseQuantity: number;
  eligibilityStatus: "Eligible" | "PartiallyAvailable" | "Excluded";
  exclusionReason?: string;
  suggestedAllocationBaseQuantity: number;
}

export interface DispatchPreparationItemDto {
  orderItemId: string;
  crop: string;
  variety?: string;
  trayType?: string;
  trayCapacity?: number;
  isCompanySeedling: boolean;
  orderedBaseQuantity: number;
  previouslyDispatchedBaseQuantity: number;
  remainingBaseQuantity: number;
  totalPhysicalBaseQuantity: number;
  totalReservedBaseQuantity: number;
  totalFillingReserveBaseQuantity: number;
  totalOpenAllocationBaseQuantity: number;
  totalDispatchableBaseQuantity: number;
  suggestedDispatchBaseQuantity: number;
  remainingAfterSuggestedDispatchBaseQuantity: number;
  unavailabilityReason?: string;
  eligibleBatches: DispatchPreparationBatchDto[];
}

export interface DispatchPreparationDto {
  orderId: string;
  orderNumber: string;
  customerName: string;
  orderStatus: string;
  items: DispatchPreparationItemDto[];
}

// ── API ────────────────────────────────────────────────────────────────────

export const dispatchApi = {
  // Queries
  getById: async (id: string): Promise<DispatchRecordDto> => {
    const res = await apiClient.get<ApiResponse<DispatchRecordWire>>(`/dispatch/${id}`);
    return normalizeDispatchRecord(res.data.data);
  },

  getByOrder: async (orderId: string): Promise<DispatchRecordSummaryDto[]> => {
    const res = await apiClient.get<ApiResponse<DispatchRecordSummaryWire[]>>(`/dispatch/order/${orderId}`);
    return (res.data.data ?? []).map(normalizeDispatchSummary);
  },

  getPreparation: async (orderId: string): Promise<DispatchPreparationDto> => {
    const res = await apiClient.get<ApiResponse<DispatchPreparationDto>>(`/dispatch/orders/${orderId}/preparation`);
    return res.data.data;
  },

  getPending: async (pageNumber = 1, pageSize = 25, search?: string): Promise<DispatchHistoryListResult> => {
    const params: Record<string, unknown> = { pageNumber, pageSize };
    if (search) params.search = search;
    const res = await apiClient.get<ApiResponse<PagedWire<DispatchRecordSummaryWire>>>(`/dispatch/pending`, { params });
    const data = res.data.data;
    return {
      items: (data.items ?? []).map(normalizeDispatchSummary),
      totalCount: data.totalCount ?? 0,
      pageNumber: data.pageNumber ?? pageNumber,
      pageSize: data.pageSize ?? pageSize,
      totalPages: data.totalPages ?? 0,
    };
  },

  getReadyForHandover: async (pageNumber = 1, pageSize = 25, search?: string): Promise<DispatchHistoryListResult> => {
    const params: Record<string, unknown> = { pageNumber, pageSize };
    if (search) params.search = search;
    const res = await apiClient.get<ApiResponse<PagedWire<DispatchRecordSummaryWire>>>(`/dispatch/ready-for-handover`, { params });
    const data = res.data.data;
    return {
      items: (data.items ?? []).map(normalizeDispatchSummary),
      totalCount: data.totalCount ?? 0,
      pageNumber: data.pageNumber ?? pageNumber,
      pageSize: data.pageSize ?? pageSize,
      totalPages: data.totalPages ?? 0,
    };
  },

  getReadyForRelease: async (pageNumber = 1, pageSize = 25, search?: string): Promise<DispatchHistoryListResult> => {
    const params: Record<string, unknown> = { pageNumber, pageSize };
    if (search) params.search = search;
    const res = await apiClient.get<ApiResponse<PagedWire<DispatchRecordSummaryWire>>>(`/dispatch/ready-for-release`, { params });
    const data = res.data.data;
    return {
      items: (data.items ?? []).map(normalizeDispatchSummary),
      totalCount: data.totalCount ?? 0,
      pageNumber: data.pageNumber ?? pageNumber,
      pageSize: data.pageSize ?? pageSize,
      totalPages: data.totalPages ?? 0,
    };
  },

  getDispatched: async (): Promise<DispatchRecordSummaryDto[]> => {
    const res = await apiClient.get<ApiResponse<DispatchRecordSummaryWire[]>>(`/dispatch/dispatched`);
    return (res.data.data ?? []).map(normalizeDispatchSummary);
  },

  getDispatchHistoryPaged: async (pageNumber = 1, pageSize = 25, search?: string): Promise<DispatchHistoryListResult> => {
    const params: Record<string, unknown> = { pageNumber, pageSize };
    if (search) params.search = search;
    const res = await apiClient.get<ApiResponse<PagedWire<DispatchRecordSummaryWire>>>(`/dispatch/history`, { params });
    const data = res.data.data;
    return {
      items: (data.items ?? []).map(normalizeDispatchSummary),
      totalCount: data.totalCount ?? 0,
      pageNumber: data.pageNumber ?? pageNumber,
      pageSize: data.pageSize ?? pageSize,
      totalPages: data.totalPages ?? 0,
    };
  },

  getTrayReturnsHistoryPaged: async (pageNumber = 1, pageSize = 25, search?: string): Promise<TrayReturnHistoryListResult> => {
    const params: Record<string, unknown> = { pageNumber, pageSize };
    if (search) params.search = search;
    const res = await apiClient.get<ApiResponse<PagedWire<TrayReturnHistorySummaryDto>>>(`/dispatch/tray-returns`, { params });
    const data = res.data.data;
    return {
      items: data.items ?? [],
      totalCount: data.totalCount ?? 0,
      pageNumber: data.pageNumber ?? pageNumber,
      pageSize: data.pageSize ?? pageSize,
      totalPages: data.totalPages ?? 0,
    };
  },

  getOutstandingTrays: async (county?: string): Promise<OutstandingTrayDto[]> => {
    const params = county ? { county } : {};
    const res = await apiClient.get<ApiResponse<OutstandingTrayWire[]>>(`/dispatch/outstanding-trays`, { params });
    return (res.data.data ?? []).map(normalizeOutstandingTray);
  },

  getOutstandingTraysPaged: async (pageNumber = 1, pageSize = 25, search?: string, county?: string): Promise<OutstandingTrayListResult> => {
    const params: Record<string, unknown> = { pageNumber, pageSize };
    if (search) params.search = search;
    if (county) params.county = county;
    const res = await apiClient.get<ApiResponse<PagedWire<OutstandingTrayWire>>>(`/dispatch/outstanding-trays`, { params });
    const data = res.data.data;
    return {
      items: (data.items ?? []).map(normalizeOutstandingTray),
      totalCount: data.totalCount ?? 0,
      pageNumber: data.pageNumber ?? pageNumber,
      pageSize: data.pageSize ?? pageSize,
      totalPages: data.totalPages ?? 0,
    };
  },

  getDeliveryNote: async (id: string): Promise<DeliveryNoteDto> => {
    const res = await apiClient.get<ApiResponse<DeliveryNoteWire>>(`/dispatch/${id}/delivery-note`);
    return normalizeDeliveryNote(res.data.data);
  },

  downloadDeliveryNotePdf: async (id: string): Promise<void> => {
    const res = await apiClient.get<Blob>(`/dispatch/${id}/delivery-note/pdf`, {
      responseType: "blob",
    });
    const fileName =
      res.headers["content-disposition"]?.match(/filename="?([^";\n]+)"?/)?.[1] ??
      `delivery-note-${id}.pdf`;
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  downloadOutstandingTraysPdf: async (county?: string): Promise<void> => {
    const params = county ? { county } : {};
    const res = await apiClient.get<Blob>(`/dispatch/outstanding-trays/pdf`, {
      params,
      responseType: "blob",
    });
    const fileName =
      res.headers["content-disposition"]?.match(/filename="?([^";\n]+)"?/)?.[1] ??
      `outstanding-trays.pdf`;
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  // Commands
  initiate: async (data: InitiateDispatchRequest, idempotencyKey: string): Promise<DispatchRecordDto> => {
    const res = await apiClient.post<ApiResponse<DispatchRecordWire>>(`/dispatch/initiate`, data, idempotencyHeader(idempotencyKey));
    return normalizeDispatchRecord(res.data.data);
  },

  confirmHandover: async (id: string, data: ConfirmHandoverRequest, idempotencyKey = generateDispatchIdempotencyKey()): Promise<DispatchRecordDto> => {
    const res = await apiClient.post<ApiResponse<DispatchRecordWire>>(`/dispatch/${id}/confirm-handover`, data, idempotencyHeader(idempotencyKey));
    return normalizeDispatchRecord(res.data.data);
  },

  acknowledgeReceipt: async (id: string, data: AcknowledgeReceiptRequest, idempotencyKey = generateDispatchIdempotencyKey()): Promise<DispatchRecordDto> => {
    const res = await apiClient.post<ApiResponse<DispatchRecordWire>>(`/dispatch/${id}/acknowledge-receipt`, data, idempotencyHeader(idempotencyKey));
    return normalizeDispatchRecord(res.data.data);
  },

  complete: async (id: string, data: CompleteDispatchRequest, idempotencyKey = generateDispatchIdempotencyKey()): Promise<DispatchRecordDto> => {
    const res = await apiClient.post<ApiResponse<DispatchRecordWire>>(`/dispatch/${id}/complete`, data, idempotencyHeader(idempotencyKey));
    return normalizeDispatchRecord(res.data.data);
  },

  confirmDelivery: async (id: string, data: ConfirmDeliveryRequest): Promise<DispatchRecordDto> => {
    const res = await apiClient.post<ApiResponse<DispatchRecordWire>>(`/dispatch/${id}/confirm-delivery`, data);
    return normalizeDispatchRecord(res.data.data);
  },

  recordTrayReturn: async (id: string, data: RecordTrayReturnRequest): Promise<TrayReturnDto> => {
    const res = await apiClient.post<ApiResponse<TrayReturnDto>>(`/dispatch/${id}/tray-returns`, data);
    return res.data.data;
  },

  getPendingTrayReturns: async (): Promise<TrayReturnDto[]> => {
    const res = await apiClient.get<ApiResponse<TrayReturnDto[]>>("/dispatch/tray-returns/pending");
    return res.data.data ?? [];
  },

  approveTrayReturn: async (id: string, approvalNotes?: string): Promise<TrayReturnDto> => {
    const res = await apiClient.post<ApiResponse<TrayReturnDto>>(`/dispatch/tray-returns/${id}/approve`, { approvalNotes });
    return res.data.data;
  },

  rejectTrayReturn: async (id: string, rejectionReason: string): Promise<TrayReturnDto> => {
    const res = await apiClient.post<ApiResponse<TrayReturnDto>>(`/dispatch/tray-returns/${id}/reject`, { rejectionReason });
    return res.data.data;
  },

  cancel: async (id: string, data: CancelDispatchRequest): Promise<DispatchRecordDto> => {
    const res = await apiClient.post<ApiResponse<DispatchRecordWire>>(`/dispatch/${id}/cancel`, data);
    return normalizeDispatchRecord(res.data.data);
  },

  // Compensation Notes
  getCompensationById: async (id: string): Promise<CompensationNoteDto> => {
    const res = await apiClient.get<ApiResponse<CompensationNoteDto>>(`/compensation-notes/${id}`);
    return res.data.data;
  },

  getCompensationByOrder: async (orderId: string): Promise<CompensationNoteSummaryDto[]> => {
    const res = await apiClient.get<ApiResponse<CompensationNoteSummaryDto[]>>(`/compensation-notes/order/${orderId}`);
    return res.data.data ?? [];
  },

  getPendingCompensations: async (): Promise<CompensationNoteSummaryDto[]> => {
    const res = await apiClient.get<ApiResponse<CompensationNoteSummaryDto[]>>(`/compensation-notes/pending`);
    return res.data.data ?? [];
  },

  raiseCompensation: async (data: RaiseCompensationNoteRequest): Promise<CompensationNoteDto> => {
    const res = await apiClient.post<ApiResponse<CompensationNoteDto>>(`/compensation-notes`, data);
    return res.data.data;
  },

  submitCompensation: async (id: string): Promise<CompensationNoteDto> => {
    const res = await apiClient.post<ApiResponse<CompensationNoteDto>>(`/compensation-notes/${id}/submit`, {});
    return res.data.data;
  },

  approveCompensation: async (id: string, data: ApproveCompensationNoteRequest): Promise<CompensationNoteDto> => {
    const res = await apiClient.post<ApiResponse<CompensationNoteDto>>(`/compensation-notes/${id}/approve`, data);
    return res.data.data;
  },

  rejectCompensation: async (id: string, data: RejectCompensationNoteRequest): Promise<CompensationNoteDto> => {
    const res = await apiClient.post<ApiResponse<CompensationNoteDto>>(`/compensation-notes/${id}/reject`, data);
    return res.data.data;
  },
};
